/**
 * Lawity Animation Presets
 * Heavy, satisfying physics for a "weighty" feel
 */

import type { Transition, Variants } from 'framer-motion';

// ============================================
// SPRING CONFIGURATIONS
// ============================================

/**
 * Gentle - For subtle UI transitions
 * Low stiffness, moderate damping = soft, slow settle
 */
export const springGentle: Transition = {
  type: 'spring',
  stiffness: 100,
  damping: 20,
  mass: 0.8,
};

/**
 * Satisfying - Primary interaction spring
 * Balanced stiffness/damping = responsive but grounded
 */
export const springSatisfying: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
  mass: 1.2,
};

/**
 * Heavy - For decision completions, dismissals
 * High stiffness, high damping = snappy "thunk"
 */
export const springHeavy: Transition = {
  type: 'spring',
  stiffness: 500,
  damping: 50,
  mass: 1.5,
};

/**
 * Bouncy - For celebrations, success states
 * Lower damping = playful overshoot
 */
export const springBouncy: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 15,
  mass: 1,
};

/**
 * Snap - For quick UI responses
 * Very high stiffness = immediate response
 */
export const springSnap: Transition = {
  type: 'spring',
  stiffness: 700,
  damping: 40,
  mass: 0.5,
};

// ============================================
// TWEEN CONFIGURATIONS
// ============================================

export const tweenGentle: Transition = {
  type: 'tween',
  duration: 0.3,
  ease: 'easeOut',
};

export const tweenFast: Transition = {
  type: 'tween',
  duration: 0.15,
  ease: 'easeOut',
};

export const tweenSlow: Transition = {
  type: 'tween',
  duration: 0.5,
  ease: [0.4, 0, 0.2, 1], // Custom ease
};

// ============================================
// VARIANT PRESETS
// ============================================

/**
 * Fade variants - Simple opacity transitions
 */
export const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: tweenGentle,
  },
  exit: {
    opacity: 0,
    transition: tweenFast,
  },
};

/**
 * Slide Up variants - Cards, modals entering from bottom
 */
export const slideUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springSatisfying,
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: tweenFast,
  },
};

/**
 * Slide Down variants - Dropdowns, menus
 */
export const slideDownVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: springSnap,
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: tweenFast,
  },
};

/**
 * Scale variants - Buttons, interactive elements
 */
export const scaleVariants: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1.02, transition: springSnap },
  tap: { scale: 0.98, transition: springSnap },
  focus: {
    scale: 1.01,
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.5)',
    transition: springSnap,
  },
};

/**
 * Card Stack variants - For decision cards
 */
export const cardStackVariants: Variants = {
  // Card enters the stack
  enter: (i: number) => ({
    opacity: 1,
    y: i * 8,
    scale: 1 - i * 0.05,
    rotateZ: 0,
    transition: springSatisfying,
  }),
  // Card is being dragged
  dragging: {
    scale: 1.02,
    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.15)',
    transition: springSnap,
  },
  // Card exits right (approve)
  exitRight: {
    x: 500,
    y: -50,
    rotate: 30,
    opacity: 0,
    transition: springHeavy,
  },
  // Card exits left (reject)
  exitLeft: {
    x: -500,
    y: -50,
    rotate: -30,
    opacity: 0,
    transition: springHeavy,
  },
};

/**
 * Modal variants - For overlays and dialogs
 */
export const modalVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: springSatisfying,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: tweenFast,
  },
};

/**
 * Backdrop variants - Modal backgrounds
 */
export const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15 },
  },
};

/**
 * List item variants - For staggered lists
 */
export const listItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      ...springSatisfying,
    },
  }),
};

/**
 * Stagger container variants
 */
export const staggerContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

/**
 * Pulse variants - For attention/alerts
 */
export const pulseVariants: Variants = {
  initial: { scale: 1, opacity: 1 },
  pulse: {
    scale: [1, 1.05, 1],
    opacity: [1, 0.8, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

/**
 * Shake variants - For errors/invalid input
 */
export const shakeVariants: Variants = {
  initial: { x: 0 },
  shake: {
    x: [-10, 10, -10, 10, 0],
    transition: {
      duration: 0.4,
      ease: 'easeInOut',
    },
  },
};

// ============================================
// GESTURE CONFIGS
// ============================================

/**
 * Drag constraints for swipeable cards
 */
export const swipeConstraints = {
  left: -150,
  right: 150,
  top: 0,
  bottom: 0,
};

/**
 * Swipe velocity threshold (pixels/second)
 */
export const SWIPE_VELOCITY_THRESHOLD = 500;

/**
 * Swipe distance threshold (pixels)
 */
export const SWIPE_DISTANCE_THRESHOLD = 100;

// ============================================
// ANIMATION HELPERS
// ============================================

/**
 * Get exit direction based on velocity and offset
 */
export function getSwipeDirection(
  offsetX: number,
  velocityX: number
): 'left' | 'right' | null {
  if (Math.abs(velocityX) > SWIPE_VELOCITY_THRESHOLD) {
    return velocityX > 0 ? 'right' : 'left';
  }
  if (Math.abs(offsetX) > SWIPE_DISTANCE_THRESHOLD) {
    return offsetX > 0 ? 'right' : 'left';
  }
  return null;
}

/**
 * Calculate rotation based on drag offset
 */
export function getDragRotation(offsetX: number, maxRotation = 15): number {
  return (offsetX / 300) * maxRotation;
}

/**
 * Calculate opacity for swipe indicators
 */
export function getSwipeIndicatorOpacity(
  offsetX: number,
  direction: 'left' | 'right'
): number {
  const normalizedOffset = direction === 'right' ? offsetX : -offsetX;
  return Math.min(Math.max(normalizedOffset / 100, 0), 1);
}

export default {
  springs: {
    gentle: springGentle,
    satisfying: springSatisfying,
    heavy: springHeavy,
    bouncy: springBouncy,
    snap: springSnap,
  },
  tweens: {
    gentle: tweenGentle,
    fast: tweenFast,
    slow: tweenSlow,
  },
  variants: {
    fade: fadeVariants,
    slideUp: slideUpVariants,
    slideDown: slideDownVariants,
    scale: scaleVariants,
    cardStack: cardStackVariants,
    modal: modalVariants,
    backdrop: backdropVariants,
    listItem: listItemVariants,
    staggerContainer: staggerContainerVariants,
    pulse: pulseVariants,
    shake: shakeVariants,
  },
};
